/**
 * Money lives in this file and nowhere else. The database column is
 * `Decimal(12,2)`; on the wire (JSON) and in Dexie it travels as a
 * **canonical decimal string**, exactly two decimal places (`"1234.50"`,
 * `"0.00"`) — see `MoneyAmount`. `JSON.parse` turns a JS `number` into a
 * float and the precision is gone before any of our code runs, so the
 * string is not a formatting choice, it is the correctness boundary.
 * Prisma's `Decimal` also serialises to a string, so this matches what the
 * API actually emits.
 *
 * All arithmetic here is exact, via `BigInt` on the amount scaled by 100
 * (i.e. hundredths — "paise" for INR). Quantities in this app are always
 * integers and prices are always 2dp, so scaled `BigInt` covers every
 * operation the app performs — there is deliberately no decimal library
 * here (this bundle ships to a phone).
 *
 * **A float must never touch the money path.** No `parseFloat`, no
 * `Number()`, no `toFixed()` on a money value, no `*` or `+` on money as
 * numbers. Convert and format only at the edges, via the helpers below.
 */
import { z } from 'zod';
/** ISO 4217 currency code, e.g. `'INR'`. Stored per row; every row is `'INR'` today, but call sites must always pass one explicitly. */
export const CurrencyCode = z.string().regex(/^[A-Z]{3}$/, 'Must be a 3-letter ISO 4217 currency code');
/** Displayed when an amount is null or absent — never `'0.00'`, never `'₹0'`. This is the common case: the source spreadsheet has a price on 2 of ~152 rows. */
export const NO_AMOUNT_LABEL = '—';
/**
 * The canonical wire/Dexie representation of a money amount: a decimal
 * string with exactly two decimal places. Every money field in this app
 * (a list price, an advance, a quoted price) is a price or an amount owed —
 * never a signed quantity — so this schema has no way to spell a negative
 * amount at all, which also rules out a stray `"-0.00"`.
 *
 * Rejects `"12.5"` (must be exactly 2dp), `"12"` (must have a decimal
 * point), `"1,234.50"` (no grouping separators — this is a wire format, not
 * a display format) and anything non-numeric. Accepts `"0.00"` and
 * `"1234.50"`.
 */
export const MoneyAmount = z
    .string()
    .regex(/^\d+\.\d{2}$/, 'Must be a nonnegative decimal amount with exactly two decimal places, e.g. "1234.50"');
/** Hundredths — every `MoneyAmount` has exactly 2 decimal places by construction. */
const MONEY_SCALE = 100n;
/**
 * Parses a canonical `MoneyAmount` string into an exact scaled `BigInt`
 * (the amount in hundredths) for arithmetic. The input is assumed to
 * already satisfy `MoneyAmount` — validate at the boundary with
 * `MoneyAmount.parse` (or in a request schema) before calling this; it is
 * not re-validated here.
 */
export function toScaledAmount(amount) {
    const parts = amount.split('.');
    const wholePart = parts[0];
    const fractionPart = parts[1];
    if (wholePart === undefined || fractionPart === undefined) {
        throw new Error(`Not a canonical MoneyAmount: "${amount}"`);
    }
    return BigInt(wholePart) * MONEY_SCALE + BigInt(fractionPart);
}
/**
 * Renders an exact scaled `BigInt` (as produced by `toScaledAmount`,
 * `lineTotal` or `sumAmounts`) back to the canonical `MoneyAmount` string.
 */
export function fromScaledAmount(scaled) {
    if (scaled < 0n) {
        throw new Error('Money amounts in this app are never negative');
    }
    const whole = scaled / MONEY_SCALE;
    const fraction = scaled % MONEY_SCALE;
    return `${whole.toString()}.${fraction.toString().padStart(2, '0')}`;
}
/**
 * `quantity × unitPrice`, exact. `quantity` is a plain nonnegative integer
 * (this app never has fractional quantities); `unitPrice` is a canonical
 * `MoneyAmount`. Both operands are integers under the hood (`quantity` and
 * the scaled `BigInt`), so the multiplication is exact.
 */
export function lineTotal(quantity, unitPrice) {
    if (!Number.isInteger(quantity) || quantity < 0) {
        throw new Error(`quantity must be a nonnegative integer, got ${quantity}`);
    }
    return fromScaledAmount(toScaledAmount(unitPrice) * BigInt(quantity));
}
/** Sums a list of canonical `MoneyAmount`s, exact. An empty list sums to `"0.00"`. */
export function sumAmounts(amounts) {
    const total = amounts.reduce((sum, amount) => sum + toScaledAmount(amount), 0n);
    return fromScaledAmount(total);
}
/**
 * A lead's (or item set's) grand total: the exact sum of `quantity × quotedPrice`
 * across its items. The one place this "sum of line totals" is computed, shared
 * by the board tiles, the lead detail card and the print report so they cannot
 * drift. An empty list totals to `"0.00"`.
 */
export function sumLineTotals(items) {
    return sumAmounts(items.map(item => lineTotal(item.quantity, item.quotedPrice)));
}
/**
 * Normalises whatever a human typed in a money field into a canonical
 * `MoneyAmount` string (`"1234.50"`), or `null` when the field is blank — blank
 * means "not entered yet", never `'0.00'`. A value that doesn't parse as a plain
 * nonnegative decimal (letters, a minus sign, thousands separators, more than 2
 * fraction digits) also returns `null`, which the caller treats the same as "not
 * entered". The app's money inputs are all `type="number" min="0"`, so the
 * browser already keeps out most of that; this is the last-mile guard before the
 * value crosses onto the wire. Pure string manipulation — no `parseFloat`,
 * `Number()` or `toFixed()` (a float anywhere in the money path is a bug).
 */
export function normalizeMoneyInput(raw) {
    const trimmed = raw.trim();
    if (trimmed === '')
        return null;
    const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(trimmed);
    if (!match)
        return null;
    const whole = match[1];
    if (whole === undefined)
        return null;
    const fraction = (match[2] ?? '').padEnd(2, '0');
    return `${whole}.${fraction}`;
}
/**
 * Formats a money amount for display. Takes the amount **and** the
 * currency — always `'INR'` today, but shaping call sites correctly now is
 * the whole reason the currency column exists (see docs/plan.md).
 *
 * The whole-rupee part is formatted via `Intl.NumberFormat('en-IN', ...)`
 * for locale-correct digit grouping and the currency symbol — passed as a
 * `BigInt`, which `Intl` treats as an exact integer, so no JS `number` ever
 * carries the value. The exact cents digits are then spliced in directly
 * from the scaled `BigInt`'s remainder. Nothing here rounds or approximates.
 *
 * A null/undefined amount formats as `'—'`, never `'0.00'` and never `'₹0'`.
 */
export function formatMoney(amount, currency) {
    if (amount === null || amount === undefined) {
        return NO_AMOUNT_LABEL;
    }
    const scaled = toScaledAmount(amount);
    const whole = scaled / MONEY_SCALE;
    const fraction = scaled % MONEY_SCALE;
    const centsSuffix = fraction.toString().padStart(2, '0');
    try {
        const groupedWhole = currencyFormatter(currency).format(whole);
        return `${groupedWhole}.${centsSuffix}`;
    }
    catch {
        return `${currency} ${whole.toString()}.${centsSuffix}`;
    }
}
/**
 * Whole rupees only, for the board's summary strip — where three figures share
 * one line on a phone and `.00` on each was enough to wrap it.
 *
 * Rounds half-up rather than truncating: a running total that reads ₹999 when
 * it is ₹1,000 misreports the direction. Still exact `BigInt` arithmetic, and
 * still display-only — every figure a buyer or the office acts on
 * (line items, quote, print report) goes through `formatMoney` at full
 * precision.
 */
export function formatMoneyWhole(amount, currency) {
    if (amount === null || amount === undefined) {
        return NO_AMOUNT_LABEL;
    }
    const scaled = toScaledAmount(amount);
    const half = MONEY_SCALE / 2n;
    const rounded = scaled < 0n ? (scaled - half) / MONEY_SCALE : (scaled + half) / MONEY_SCALE;
    try {
        return currencyFormatter(currency).format(rounded);
    }
    catch {
        return `${currency} ${rounded.toString()}`;
    }
}
/**
 * `Intl.NumberFormat` construction (locale + currency-data lookup) costs far
 * more than a `.format()` call, and `formatMoney` runs once or twice per row in
 * hot list renders (a ~150-card catalogue, every board lead, every printed line
 * item). Cache one formatter per currency — there is effectively one (`'INR'`)
 * — so a full render reuses it instead of rebuilding it hundreds of times.
 */
const CURRENCY_FORMATTERS = new Map();
function currencyFormatter(currency) {
    const cached = CURRENCY_FORMATTERS.get(currency);
    if (cached !== undefined)
        return cached;
    const formatter = new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    });
    CURRENCY_FORMATTERS.set(currency, formatter);
    return formatter;
}
//# sourceMappingURL=money.js.map